/**
 * The Screener's running deployment budget line.
 *
 * Sits in the queue action bar, beside the checkboxes, and reports three things as the CIO
 * builds a basket by hand: what this session has already queued, what is ticked and sized, and
 * what is left of the figure he typed in the sizing box.
 *
 * It is a pure readout. It renders no control, exposes no callback, and returns nothing a caller
 * could disable a button from — going over the budget is the CIO's call and must never gate
 * Queue Selected. See src/lib/screenerBudget.ts for why the field exists at all.
 *
 * Extracted from ScreenView so it can be render-tested: the uirender harness covers this page's
 * CONTEXT panels only, so nothing inside the ranked-contracts card is visually gated today.
 */

import React from "react";
import type { ScreenerBudget } from "@lib/screenerBudget";

const usd = (n: number | null | undefined) =>
  typeof n === "number"
    ? n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 })
    : "—";

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

export function ScreenerBudgetLine({
  tally,
  textPrimary,
}: {
  tally: ScreenerBudget;
  /** The card's primary text colour, so the figures read as figures against muted labels. */
  textPrimary?: string;
}) {
  if (tally.idle) return null;
  const strong = textPrimary ? { color: textPrimary } : undefined;

  return (
    <div className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1" data-testid="screener-budget-line">
      {tally.queuedLines > 0 ? (
        <span title="BUY tickets this page sent to the Trading Desk since it was loaded, at the size they were sent.">
          Queued this session{" "}
          <span className="font-semibold" style={strong}>
            {usd(tally.queuedNotional)}
          </span>{" "}
          · {tally.queuedContracts} {plural(tally.queuedContracts, "contract", "contracts")}
        </span>
      ) : null}

      <span title="Ticked and sized, not yet queued. Quantity × mid × 100 — an estimate, not a fill.">
        Basket at mid{" "}
        <span className="font-semibold" style={strong}>
          {usd(tally.basketNotional)}
        </span>{" "}
        · {tally.basketContracts} {plural(tally.basketContracts, "contract", "contracts")}
      </span>

      {tally.budget != null ? (
        <span
          // data-tone is the machine-readable half of the colour: jsdom does not parse
          // rgb(var(--token)), so a test cannot otherwise tell amber from ordinary text.
          data-tone={tally.over ? "warn" : "neutral"}
          style={tally.over ? { color: "rgb(var(--c-warn))" } : undefined}
          title="Your own deployment figure. Nothing on this page is blocked by it."
        >
          {tally.over ? (
            <>
              Over your budget by <span className="font-semibold">{usd(tally.overBy)}</span> ·{" "}
              {usd(tally.budget)} typed
            </>
          ) : (
            <>
              Budget left{" "}
              <span className="font-semibold" style={strong}>
                {usd(tally.left)}
              </span>{" "}
              of {usd(tally.budget)}
            </>
          )}
        </span>
      ) : null}

      {/* Chosen lines this total cannot price. Disclosed rather than dropped: a row silently
          worth $0 understates the basket at the moment the CIO is judging whether he has room. */}
      {tally.unpricedLines > 0 ? (
        <span data-tone="warn" style={{ color: "rgb(var(--c-warn))" }}>
          {tally.unpricedLines} chosen {plural(tally.unpricedLines, "line has", "lines have")} no usable
          mid and {plural(tally.unpricedLines, "is", "are")} not in this total
        </span>
      ) : null}
    </div>
  );
}

export default ScreenerBudgetLine;
